import type { DatasetRecord, JsonScalar } from "../contracts/types";
import { LIMITS } from "./limits";

export interface DiffCell {
  field: string;
  before: JsonScalar;
  after: JsonScalar;
  changed: boolean;
}

export interface DiffRow {
  index: number;
  cells: DiffCell[];
}

export interface DiffSummary {
  totalRows: number;
  previewRows: number;
  totalCells: number;
  changedCells: number;
  changedFields: string[];
}

export interface DiffResult {
  rows: DiffRow[];
  summary: DiffSummary;
}

/**
 * Genera la vista previa/diff (P13-R2/P13-R3) entre el dataset original y el
 * enmascarado, acotada a `LIMITS.maxDiffPreviewRows` filas para que la UI
 * quede legible y rápida incluso con datasets grandes. El resumen sí cuenta
 * los cambios sobre TODAS las filas procesadas, no solo las mostradas.
 */
export function previewDiff(original: DatasetRecord[], masked: DatasetRecord[], fieldOrder: string[]): DiffResult {
  const changedFieldsSet = new Set<string>();
  let changedCells = 0;
  let totalCells = 0;

  const rows: DiffRow[] = [];
  for (let index = 0; index < original.length; index += 1) {
    const originalRow = original[index]!;
    const maskedRow = masked[index]!;
    const cells: DiffCell[] = [];
    for (const field of fieldOrder) {
      const before = originalRow[field] ?? null;
      const after = maskedRow[field] ?? null;
      const changed = before !== after;
      totalCells += 1;
      if (changed) {
        changedCells += 1;
        changedFieldsSet.add(field);
      }
      if (index < LIMITS.maxDiffPreviewRows) {
        cells.push({ field, before, after, changed });
      }
    }
    if (index < LIMITS.maxDiffPreviewRows) {
      rows.push({ index, cells });
    }
  }

  return {
    rows,
    summary: {
      totalRows: original.length,
      previewRows: rows.length,
      totalCells,
      changedCells,
      changedFields: [...changedFieldsSet],
    },
  };
}
